import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Mail, RefreshCw, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { StewardReviewPanel } from "@/components/succession/StewardReviewPanel";
import { useSuccessionStageState, humanSuccessionStatus } from "@/lib/succession/stages";

const STAGES = [
  { key: "profile", label: "Profile", teach: "Your family's picture, told plainly." },
  { key: "path", label: "Path", teach: "Your successor learns and gets ready." },
  { key: "decisions", label: "Decisions", teach: "The choices that shape your plan." },
  { key: "documents", label: "Documents", teach: "Your papers, ready to sign." },
  { key: "execution", label: "Execution", teach: "Where it all gets signed and filed." },
  { key: "rhythm", label: "Rhythm", teach: "The yearly check that keeps it real." },
];

interface InviteRow {
  id: string;
  invited_email: string;
  invite_error: string | null;
  successor_name: string | null;
  officer_role_to_succeed: string | null;
}

export function SuccessionPathCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const s = useSuccessionStageState(user?.id);
  const [invite, setInvite] = useState<InviteRow | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    if (!s.profileId) return;
    (async () => {
      const { data } = await supabase
        .from("succession_successors")
        .select("id, invited_email, invite_error, successor_name, officer_role_to_succeed")
        .eq("profile_id", s.profileId)
        .order("created_at", { ascending: false })
        .limit(1).maybeSingle();
      setInvite((data as InviteRow) || null);
    })();
  }, [s.profileId]);

  async function retryInvite() {
    if (!invite || !s.unaId) return;
    setRetrying(true);
    try {
      const { error } = await supabase.functions.invoke("send-successor-invitation", {
        body: {
          successor_id: invite.id,
          email: invite.invited_email,
          successor_name: invite.successor_name,
          association_name: s.unaName || null,
          officer_role_to_succeed: invite.officer_role_to_succeed || "Principal Executive Officer",
          baseUrl: window.location.origin,
        },
      });
      if (error) throw error;
      toast.success("Invitation sent.");
    } catch {
      toast.error("We could not send the invitation. Please try again in a moment.");
    } finally {
      setRetrying(false);
    }
  }

  const pct = Math.round((s.completedCount / STAGES.length) * 100);
  const circumference = 2 * Math.PI * 42;
  const dashOffset = circumference * (1 - pct / 100);
  const allDone = s.completedCount === STAGES.length;

  const unlockedArr = [true, s.stage2Unlocked, s.stage3Unlocked, s.stage4Unlocked, s.stage5Unlocked, s.stage6Unlocked];
  const doneArr = [s.stage1Done, s.stage2Done, s.stage3Done, s.stage4Done, s.stage5Done, s.stage6Done];

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-start gap-5">
        <div className="relative shrink-0">
          <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
            <circle cx="50" cy="50" r="42" strokeWidth="8" className="stroke-muted fill-none" />
            <circle
              cx="50" cy="50" r="42" strokeWidth="8"
              className="stroke-primary fill-none transition-all duration-500"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-xl font-semibold">{s.completedCount}/6</div>
              <div className="text-[10px] text-muted-foreground">stages</div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-serif">Succession Path</h3>
          <p className="text-sm text-muted-foreground">
            Getting the next steward of your family Association ready.
          </p>
          {s.loading ? null : s.isPaused ? (
            <p className="text-xs mt-2 text-amber-700">Paused for review. Our team will be in touch.</p>
          ) : s.profileStatus == null ? (
            <p className="text-xs mt-2 text-muted-foreground">Start with your Succession Profile.</p>
          ) : allDone ? (
            <p className="text-xs mt-2 text-emerald-700">Your plan is alive. Keep the yearly rhythm.</p>
          ) : (
            <p className="text-xs mt-2 text-muted-foreground">{humanSuccessionStatus(s.profileStatus)}</p>
          )}
        </div>
      </div>

      {invite?.invite_error && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-2">
          <div className="flex items-start gap-2">
            <Mail className="h-4 w-4 text-amber-700 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-amber-900">
                The invitation to {invite.invited_email} did not send.
              </p>
              <p className="text-xs text-amber-800">
                This happens sometimes. Nothing you entered is lost. You can try again now.
              </p>
              <Button size="sm" variant="outline" onClick={retryInvite} disabled={retrying}>
                {retrying ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-2" />
                )}
                Retry invitation
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {STAGES.map((stage, i) => {
          const unlocked = unlockedArr[i];
          const done = doneArr[i];
          const showPreNote = i === 2 && s.stage3Unlocked && !s.stage2Done;
          const onClick = () => {
            if (!unlocked) return;
            if (i === 0) navigate("/succession");
            else if (i === 1) setReviewOpen(true);
            else if (i === 2) navigate("/succession/decisions");
            else if (i === 3) navigate("/succession/documents");
            else if (i === 4) navigate("/succession/execution");
            else if (i === 5) navigate("/succession/rhythm");
          };
          return (
            <button
              key={stage.key}
              disabled={!unlocked}
              onClick={onClick}
              className={`text-left rounded-md border p-3 transition ${
                unlocked ? "hover:border-primary hover:bg-accent cursor-pointer" : "opacity-60 cursor-not-allowed"
              } ${done ? "border-primary/60 bg-primary/5" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{i + 1}. {stage.label}</span>
                {done ? (
                  <Check className="h-3 w-3 text-primary" />
                ) : !unlocked ? (
                  <Lock className="h-3 w-3 text-muted-foreground" />
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">{stage.teach}</p>
              {showPreNote && (
                <p className="text-[10px] text-muted-foreground mt-1 italic">You can start now while the path is still in progress.</p>
              )}
            </button>
          );
        })}
      </div>

      {s.unaId && (
        <StewardReviewPanel
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          associationId={s.unaId}
          associationName={s.unaName}
          onCompleted={() => { /* hook re-fetch handled on next mount */ }}
        />
      )}
    </Card>
  );
}
